// Màn hình "KPI Chất lượng vận hành" — scorecard tháng theo cửa hàng từ hệ thống
// ops-quality (CSAT × GR × Bảo hành → tổng KPI, thưởng/phạt) + trend điểm KPI
// theo ngày snapshot. View-only: số liệu do Worker ops-quality tính, đây chỉ đọc.
import { KpiCard } from '../components/KpiCard';
import { Tag } from '../components/Tag';
import { ChartBox, chartDataset } from '../components/ChartBox';
import type { OpsQualityResponse, OpsScorecardRow } from '../types';
import { asNumber, money, monthLabel, num, opsStatusLabel, opsStatusTone, pct1, storeSeriesColor, uniq } from '../utils';

interface OpsQualityScreenProps {
  data: OpsQualityResponse | null;
  loaded: boolean;
  busy: boolean;
  month: string;
  onMonth: (month: string) => void;
}

const kpiTone = (v: number): 'green' | 'blue' | 'red' => (v >= 110 ? 'green' : v >= 100 ? 'blue' : 'red');

export function OpsQualityScreen({ data, loaded, busy, month, onMonth }: OpsQualityScreenProps) {
  if (busy && !loaded) return <div className="card empty">Đang tải KPI chất lượng vận hành...</div>;
  if (!loaded) return <div className="card empty">Mở mục KPI Chất lượng vận hành để tải dữ liệu.</div>;

  const months = data?.meta?.months || [];
  const scorecard: OpsScorecardRow[] = data?.scorecard || [];
  const trend = data?.trend || [];
  const csat = data?.csat || [];
  const gr = data?.gr || [];
  const warranty = data?.warranty || [];
  const targets = data?.targets || [];

  const csatTot = csat.reduce((a, r) => a + asNumber(r.tot), 0);
  const csatXau = csat.reduce((a, r) => a + asNumber(r.xau), 0);
  const csatPct = csatTot + csatXau ? (100 * csatTot) / (csatTot + csatXau) : 0;
  const grTot = gr.reduce((a, r) => a + asNumber(r.review_tot), 0);
  const grDon = gr.reduce((a, r) => a + asNumber(r.don_tao), 0);
  const grPct = grDon ? (100 * grTot) / grDon : 0;
  const whTotal = warranty.reduce((a, r) => a + asNumber(r.total), 0);
  const whSumDays = warranty.reduce((a, r) => a + asNumber(r.avg_days) * asNumber(r.total), 0);
  const whAvg = whTotal ? whSumDays / whTotal : 0;
  const penaltyTotal = scorecard.reduce((a, r) => a + asNumber(r.total_penalty), 0);
  const kpiAvg = scorecard.length ? scorecard.reduce((a, r) => a + asNumber(r.total_kpi), 0) / scorecard.length : 0;

  // Target hiển thị (giống nhau giữa các store trừ Bảo hành).
  const targetOf = (code: string) => uniq(targets.filter(t => t.kpi_code === code).map(t => String(t.target_value))).join(' / ');

  // Trend: mỗi store 1 line theo snapshot_date.
  const trendDates = uniq(trend.map(r => r.snapshot_date));
  const trendStores = uniq(trend.map(r => r.store_code));
  const trendSets = trendStores.map(code => {
    const byDate = new Map(trend.filter(r => r.store_code === code).map(r => [r.snapshot_date, asNumber(r.total_kpi)]));
    return chartDataset(code, trendDates.map(d => byDate.get(d) ?? NaN), storeSeriesColor(code), 'line');
  });

  return (
    <>
      <section className="filters">
        <div className="actions start">
          <span className="kpi-label">Tháng</span>
          {months.map(m => (
            <button key={m} className={`tab ${month === m ? 'active' : ''}`} onClick={() => onMonth(m)}>{monthLabel(m)}</button>
          ))}
        </div>
      </section>

      <section className="grid kpis">
        <KpiCard label="Điểm KPI trung bình" value={pct1(kpiAvg)} sub={`${scorecard.length} cửa hàng · MTD ${data?.meta?.snapshot_date || '-'}`} tone={kpiTone(kpiAvg)} />
        <KpiCard label="CSAT toàn hệ thống" value={pct1(csatPct)} sub={`${num(csatTot)} tốt / ${num(csatXau)} xấu · target ${targetOf('04-CSAT') || '95'}%`} tone={csatPct >= 95 ? 'green' : 'red'} />
        <KpiCard label="Chuyển đổi review" value={pct1(grPct)} sub={`${num(grTot)} review tốt / ${num(grDon)} đơn · target ${targetOf('01-ConvReview') || '15'}%`} tone={grPct >= 15 ? 'green' : 'amber'} />
        <KpiCard label="Bảo hành TB" value={`${Math.round(whAvg * 100) / 100} ngày`} sub={`${num(whTotal)} phiếu · target ${targetOf('10-WarrantyTime') || '-'} ngày`} tone={whAvg <= 11 ? 'green' : 'red'} />
        <KpiCard label="Tổng phạt tháng" value={money(penaltyTotal)} sub="phiếu phạt khiếu nại + TCĐT" tone={penaltyTotal ? 'red' : 'green'} />
      </section>

      <section className="card space">
        <div className="section-title">
          <h2>Bảng xếp hạng KPI theo cửa hàng · {monthLabel(month)}</h2>
          <span>Tổng KPI = GR 10% + CSAT 40% + Bảo hành 50% (trọng số ops-quality) · snapshot {data?.meta?.snapshot_date || '-'}</span>
        </div>
        {scorecard.length === 0 ? (
          <div className="card empty">Chưa có snapshot scorecard cho {monthLabel(month)} (scorecard bắt đầu từ T6/2026).</div>
        ) : (
          <div className="scroll">
            <table>
              <thead><tr>
                <th className="right">Hạng</th><th>Cửa hàng</th><th className="right">GR %</th><th className="right">CSAT %</th>
                <th className="right">Bảo hành %</th><th className="right">Tổng KPI</th><th>Xếp loại</th>
                <th className="right">Thưởng</th><th className="right">Phạt</th>
              </tr></thead>
              <tbody>{scorecard.map(r => (
                <tr key={r.store_code}>
                  <td className="right tnum"><b>{num(r.rank)}</b></td>
                  <td><b>{r.store_code}</b><div className="muted">{r.store_name}</div></td>
                  <td className="right tnum">{r.gr_pct == null ? <span className="muted">-</span> : pct1(r.gr_pct)}</td>
                  <td className="right tnum">{r.csat_pct == null ? <span className="muted">-</span> : pct1(r.csat_pct)}</td>
                  <td className="right tnum">{r.warranty_pct == null ? <span className="muted">-</span> : pct1(r.warranty_pct)}</td>
                  <td className="right tnum"><b>{pct1(r.total_kpi)}</b></td>
                  <td><Tag tone={opsStatusTone(r.status)}>{opsStatusLabel(r.status)}</Tag></td>
                  <td className="right tnum">{pct1(r.bonus_rate)}</td>
                  <td className="right tnum">{asNumber(r.total_penalty) ? <Tag tone="red">{money(r.total_penalty)}</Tag> : <span className="muted">0</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      <section className="grid split atop space">
        <div className="card">
          <div className="section-title">
            <h2>Diễn biến điểm KPI trong tháng</h2>
            <span>MTD theo ngày snapshot · mỗi cửa hàng 1 đường</span>
          </div>
          {trendDates.length === 0 ? (
            <div className="card empty">Chưa có dữ liệu trend cho tháng này.</div>
          ) : (
            <div className="chart"><ChartBox type="line" labels={trendDates.map(d => d.slice(8))} datasets={trendSets} /></div>
          )}
        </div>
        <div className="card">
          <div className="section-title">
            <h2>Số thực 3 cấu phần KPI</h2>
            <span>CSAT (tốt/xấu) · GR (review/đơn) · Bảo hành (ngày TB)</span>
          </div>
          <div className="scroll">
            <table>
              <thead><tr>
                <th>Cửa hàng</th><th className="right">CSAT tốt</th><th className="right">CSAT xấu</th>
                <th className="right">Review tốt</th><th className="right">Đơn tạo</th>
                <th className="right">Phiếu BH</th><th className="right">Ngày TB</th><th className="right">KPI BH</th>
              </tr></thead>
              <tbody>{uniq([...csat.map(r => r.store_code), ...gr.map(r => r.store_code), ...warranty.map(r => r.store_code)]).map(code => {
                const c = csat.find(r => r.store_code === code);
                const g = gr.find(r => r.store_code === code);
                const w = warranty.find(r => r.store_code === code);
                return (
                  <tr key={code}>
                    <td><b>{code}</b></td>
                    <td className="right tnum">{c ? num(c.tot) : <span className="muted">-</span>}</td>
                    <td className="right tnum">{c && asNumber(c.xau) ? <Tag tone="red">{num(c.xau)}</Tag> : <span className="muted">{c ? '0' : '-'}</span>}</td>
                    <td className="right tnum">{g ? num(g.review_tot) : <span className="muted">-</span>}</td>
                    <td className="right tnum">{g ? num(g.don_tao) : <span className="muted">-</span>}</td>
                    <td className="right tnum">{w ? num(w.total) : <span className="muted">-</span>}</td>
                    <td className="right tnum">{w ? num(Math.round(asNumber(w.avg_days) * 100) / 100) : <span className="muted">-</span>}</td>
                    <td className="right">{w ? <Tag tone={asNumber(w.kpi_pct) >= 100 ? 'green' : 'red'}>{pct1(w.kpi_pct)}</Tag> : <span className="muted">-</span>}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
